/**
 * The variants table — the matrix's rows, per the TradeCore mockup: "N
 * variants generated", Generate Variants, Bulk Edit, Export CSV, and a
 * selectable table of SKU · <one column per attribute> · Barcode · Weight ·
 * Qty · Status.
 *
 * Fully controlled by the form. Generation runs client-side
 * (`generateVariants` + `mergeVariants` from the domain's pure subpath) and
 * MERGES: combinations that survive keep their edited row, new ones append,
 * stale ones drop — reported in one consolidated toast, never silently.
 *
 * The Status pill is derived and not editable (ADR-0061). Its source is
 * `Σ(pieces of this variant in one bundle × bundles held)` over the listing's
 * pricing entries — the operator records that once, in the Bundle Builder.
 * The raw number is NOT a column: it only ever restated what the pricing card
 * already shows (composition × packs), so the table keeps the reading and
 * drops the arithmetic. A variant no bundle mentions renders no pill at all
 * rather than "Out of stock": untracked and zero are different facts (spec 47
 * R5), and painting an unpriced product's variants red would be a lie.
 *
 * Dialogs mount only while open (the `pricing-entry-list` lesson: a mounted
 * dialog carries the previous open's state). `onChange` is called once per
 * user action with a complete next array — never incrementally — which is
 * what keeps the `product-images-field` stale-closure hazard out.
 */
import { useState } from "react";
import { Download, Pencil, SlidersHorizontal, Trash2, Wand2 } from "lucide-react";
import {
  Button,
  Checkbox,
  ConfirmDialog,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Input,
  StatusPill,
  toast,
} from "@flint/ui";
import {
  MAX_VARIANTS,
  generateVariants,
  mergeVariants,
  variantKey,
  variantStockState,
  variantQtyFromBundles,
  type BundleStockSource,
  type VariantAttribute,
  type VariantDraft,
  type VariantStockState,
} from "@flint/domain/commerce/store/variant-matrix";

/**
 * The mockup's pill vocabulary. Green "In stock" deviates from the store
 * module's usual silence-when-fine — deliberate here: in a grid whose whole
 * point is per-row availability, an empty cell reads as "unknown", not "fine".
 */
const STOCK_PILL: Record<VariantStockState, { label: string; tone: "ok" | "warn" | "bad" } | null> =
  {
    untracked: null,
    "in-stock": { label: "In stock", tone: "ok" },
    "low-stock": { label: "Low stock", tone: "warn" },
    "out-of-stock": { label: "Out of stock", tone: "bad" },
  };

export interface VariantsFieldProps {
  value: VariantDraft[];
  onChange: (next: VariantDraft[]) => void;
  /** The axes, watched from the form — they name the table's middle columns. */
  attributes: VariantAttribute[];
  /** Base for defaulted variant SKUs (the item's own SKU). */
  baseSku: string;
  /**
   * The listing's pricing entries, watched from the form — the source of the
   * derived Qty column (ADR-0061). Read-only here; the mirror of
   * `ProductPackFields` passing `variants` down into the pricing list.
   */
  pricingEntries: readonly BundleStockSource[];
}

export function VariantsField({
  value,
  onChange,
  attributes,
  baseSku,
  pricingEntries,
}: VariantsFieldProps) {
  // Recomputed each render off the watched entries, so editing a bundle's
  // quantity updates this table live — both fields sit in one form.
  const derivedQty = variantQtyFromBundles(pricingEntries);
  const qtyOf = (v: VariantDraft): number | undefined =>
    derivedQty.get(variantKey(v.attributeValues));

  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  /**
   * Combinations the operator deleted this session — `mergeVariants`'s
   * tombstones, so Generate can't resurrect them (the row-derived signal
   * alone can't protect a single-axis matrix). Deliberately React state, not
   * form state: it is an editing-session memory, not data to persist.
   */
  const [deleted, setDeleted] = useState<Record<string, string>[]>([]);

  const axisNames = attributes
    .map((a) => a.name.trim())
    .filter((n, i, all) => n.length > 0 && all.indexOf(n) === i);

  const allSelected = value.length > 0 && value.every((v) => selected.has(variantKey(v.attributeValues)));

  function generate() {
    const generated = generateVariants(attributes, baseSku);
    if (generated.length === 0) {
      // Removing every attribute and regenerating is how an operator says
      // "this product has no variants" — the only path to an empty matrix,
      // since rows are not individually deletable. Confirmed rather than
      // done, because it discards every row and their edits.
      if (value.length > 0) {
        setConfirmClear(true);
        return;
      }
      toast.error("Nothing to generate", {
        description: "Add at least one attribute with values first.",
      });
      return;
    }
    // A tombstone lives only while every one of its values is still a chip —
    // removing and re-adding a value's chip is the restore path, and a pruned
    // tombstone is what lets the re-added value's combinations regenerate.
    const axisValues = new Map(
      attributes.map((a) => [a.name.trim(), new Set(a.values.map((v) => v.trim()))]),
    );
    const liveTombstones = deleted.filter((av) =>
      Object.entries(av).every(([axis, v]) => axisValues.get(axis)?.has(v) ?? false),
    );
    const { variants, added, removed, kept, leftOut } = mergeVariants(
      value,
      generated,
      new Set(liveTombstones.map(variantKey)),
    );
    setDeleted(liveTombstones);
    onChange(variants);
    setSelected(new Set());
    const parts = [
      added > 0 ? `${added} added` : null,
      removed > 0 ? `${removed} removed` : null,
      kept > 0 ? `${kept} kept` : null,
      leftOut > 0
        ? `${leftOut} deleted ${leftOut === 1 ? "combination" : "combinations"} left out`
        : null,
    ].filter(Boolean);
    toast.success(`${variants.length} variants generated`, {
      description: `${parts.join(", ")}. Saved when you save the product.`,
    });
    if (generated.length >= MAX_VARIANTS) {
      toast.error(`Capped at ${MAX_VARIANTS} variants`, {
        description: "Trim attribute values — the full matrix exceeds the maximum.",
      });
    }
  }

  function exportCsv() {
    // Identity and physical facts only — the same columns the table shows. Stock
    // is not among them: it belongs to the bundles, and exporting a derived
    // figure here would put a second, snapshot-in-time answer into circulation.
    const header = ["SKU", ...axisNames, "Barcode", "Weight (g)"];
    const rows = value.map((v) => [
      v.sku,
      ...axisNames.map((n) => v.attributeValues[n] ?? ""),
      v.barcode,
      v.weightGrams,
    ]);
    const csv = [header, ...rows].map((r) => r.map(csvCell).join(",")).join("\r\n");
    // Blob → anchor → revoke, the list-export.ts tail. CSV rather than XLSX:
    // this is an interchange payload, and lazy-loading 700 KB of ExcelJS for a
    // few dozen rows is the wrong trade.
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "variants.csv";
    document.body.append(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-ink">
          <span className="font-mono tabular-nums font-semibold">{value.length}</span> variants
          generated
        </p>
        <div className="flex flex-wrap gap-2">
          <Button intent="secondary" size="md" onClick={generate}>
            <Wand2 size={14} aria-hidden />
            Generate Variants
          </Button>
          <Button
            intent="secondary"
            size="md"
            disabled={selected.size === 0}
            onClick={() => setBulkOpen(true)}
          >
            <SlidersHorizontal size={14} aria-hidden />
            Bulk Edit{selected.size > 0 ? ` (${selected.size})` : ""}
          </Button>
          {/* No confirm on either delete: the removal is staged until Save
              (Cancel discards it), and the toast keeps it visible. The
              whole-matrix clear keeps its ConfirmDialog — that one destroys
              every edit at once. */}
          <Button
            intent="secondary"
            size="md"
            disabled={selected.size === 0}
            className="text-bad"
            onClick={() => {
              const gone = value.filter((v) => selected.has(variantKey(v.attributeValues)));
              onChange(value.filter((v) => !selected.has(variantKey(v.attributeValues))));
              setDeleted([...deleted, ...gone.map((v) => v.attributeValues)]);
              setSelected(new Set());
              toast.success(`${gone.length} ${gone.length === 1 ? "variant" : "variants"} removed`, {
                description:
                  "Saved when you save the product. Regenerating won't bring them back.",
              });
            }}
          >
            <Trash2 size={14} aria-hidden />
            Delete{selected.size > 0 ? ` (${selected.size})` : ""}
          </Button>
          <Button intent="secondary" size="md" disabled={value.length === 0} onClick={exportCsv}>
            <Download size={14} aria-hidden />
            Export CSV
          </Button>
        </div>
      </div>

      {value.length === 0 ? (
        <div className="rounded-sm border border-dashed border-line px-4 py-6 text-center">
          <p className="text-sm text-muted">
            No variants yet — add attributes and click “Generate Variants”.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded border border-line">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-muted">
              <tr>
                <th className="w-10 px-3 py-3">
                  <Checkbox
                    checked={allSelected}
                    aria-label="Select all variants"
                    onCheckedChange={(next) =>
                      setSelected(
                        next === true
                          ? new Set(value.map((v) => variantKey(v.attributeValues)))
                          : new Set(),
                      )
                    }
                  />
                </th>
                <th className="px-3 py-3 font-medium">SKU</th>
                {axisNames.map((n) => (
                  <th key={n} className="px-3 py-3 font-medium">
                    {n}
                  </th>
                ))}
                <th className="px-3 py-3 font-medium">Barcode</th>
                <th className="px-3 py-3 text-right font-medium">Weight (g)</th>
                <th className="px-3 py-3 font-medium">Status</th>
                <th className="px-3 py-3">
                  <span className="sr-only">Edit</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {value.map((v, i) => {
                const key = variantKey(v.attributeValues);
                const pill = STOCK_PILL[variantStockState(qtyOf(v))];
                return (
                  <tr key={key} className="border-b border-line last:border-0 hover:bg-raised">
                    <td className="px-3 py-2.5">
                      <Checkbox
                        checked={selected.has(key)}
                        aria-label={`Select ${v.sku || key}`}
                        onCheckedChange={(next) => {
                          const copy = new Set(selected);
                          if (next === true) copy.add(key);
                          else copy.delete(key);
                          setSelected(copy);
                        }}
                      />
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs">{v.sku || "—"}</td>
                    {axisNames.map((n) => (
                      <td key={n} className="px-3 py-2.5 text-ink-2">
                        {v.attributeValues[n] ?? "—"}
                      </td>
                    ))}
                    <td className="px-3 py-2.5 font-mono text-xs">{v.barcode || "—"}</td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums">
                      {v.weightGrams || "—"}
                    </td>
                    <td className="px-3 py-2.5">
                      {pill ? (
                        <StatusPill tone={pill.tone} size="sm" dot>
                          {pill.label}
                        </StatusPill>
                      ) : (
                        <span className="text-xs text-muted">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-0.5">
                        <button
                          type="button"
                          aria-label={`Edit ${v.sku || key}`}
                          className="rounded-sm p-1.5 text-muted transition-colors hover:bg-paper hover:text-ink"
                          onClick={() => setEditIndex(i)}
                        >
                          <Pencil className="size-4" aria-hidden />
                        </button>
                        <button
                          type="button"
                          aria-label={`Delete ${v.sku || key}`}
                          className="rounded-sm p-1.5 text-muted transition-colors hover:bg-paper hover:text-bad"
                          onClick={() => {
                            onChange(value.filter((_, idx) => idx !== i));
                            setDeleted([...deleted, v.attributeValues]);
                            const copy = new Set(selected);
                            copy.delete(key);
                            setSelected(copy);
                            toast.success("Variant removed", {
                              description:
                                "Saved when you save the product. Regenerating won't bring it back.",
                            });
                          }}
                        >
                          <Trash2 className="size-4" aria-hidden />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editIndex !== null && value[editIndex] ? (
        <VariantRowDialog
          variant={value[editIndex]}
          onClose={() => setEditIndex(null)}
          onSave={(next) => {
            onChange(value.map((v, i) => (i === editIndex ? next : v)));
            setEditIndex(null);
          }}
        />
      ) : null}

      <ConfirmDialog
        open={confirmClear}
        onOpenChange={setConfirmClear}
        title={`Remove all ${value.length} variants?`}
        description="This product has no attributes left, so it has no combinations. The rows and any edits on them are discarded when you save."
        confirmLabel="Remove variants"
        onConfirm={async () => {
          onChange([]);
          setSelected(new Set());
          // A cleared matrix is a fresh start — stale tombstones would
          // mysteriously punch holes in the next generation.
          setDeleted([]);
          setConfirmClear(false);
          toast.success("Variants removed", {
            description: "Saved when you save the product.",
          });
        }}
      />

      {bulkOpen ? (
        <BulkEditDialog
          count={selected.size}
          onClose={() => setBulkOpen(false)}
          onApply={(patch) => {
            const count = selected.size;
            onChange(
              value.map((v) =>
                selected.has(variantKey(v.attributeValues)) ? { ...v, ...patch } : v,
              ),
            );
            setBulkOpen(false);
            // The operation on those rows is done — a selection that lingers
            // invites a second accidental bulk action (operator request).
            setSelected(new Set());
            toast.success(`${count} variants updated`, {
              description: "Saved when you save the product.",
            });
          }}
        />
      ) : null}
    </div>
  );
}

/** Form drafts → wire shape: blanks become absent, numerics become numbers. */
export function variantsToWire(drafts: readonly VariantDraft[]): {
  attributeValues: Record<string, string>;
  sku?: string;
  barcode?: string;
  weightGrams?: number;
}[] {
  return drafts.map((v) => ({
    attributeValues: v.attributeValues,
    ...(v.sku.trim() ? { sku: v.sku.trim() } : {}),
    ...(v.barcode.trim() ? { barcode: v.barcode.trim() } : {}),
    ...(v.weightGrams.trim() ? { weightGrams: Number(v.weightGrams) } : {}),
  }));
}

/** Wire rows → form drafts: nulls become blanks, numbers become strings. */
export function variantsFromWire(
  rows: readonly {
    attributeValues: Record<string, string>;
    sku: string | null;
    barcode: string | null;
    weightGrams: number | null;
  }[],
): VariantDraft[] {
  return rows.map((r) => ({
    attributeValues: r.attributeValues,
    sku: r.sku ?? "",
    barcode: r.barcode ?? "",
    weightGrams: r.weightGrams === null ? "" : String(r.weightGrams),
  }));
}

/**
 * RFC-4180 quoting, plus a formula-injection guard: a cell starting with
 * `=`, `+`, `-` or `@` executes as a formula when the CSV is opened in
 * Excel/Sheets, so it gets a leading apostrophe (the spreadsheet convention
 * for "literal text"). Low stakes — operators export their own data — but a
 * variant value like `=SUM(...)` should never run on open (PR #238, L1).
 */
export function csvCell(raw: string): string {
  const guarded = /^[=+\-@]/.test(raw) ? `'${raw}` : raw;
  return /[",\r\n]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
}

function VariantRowDialog({
  variant,
  onClose,
  onSave,
}: {
  variant: VariantDraft;
  onClose: () => void;
  onSave: (next: VariantDraft) => void;
}) {
  const [draft, setDraft] = useState<VariantDraft>(variant);
  const combo = Object.values(variant.attributeValues).join(" · ");

  return (
    <DialogRoot open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Edit variant</DialogTitle>
          <DialogDescription>
            {combo} — the combination itself is fixed; regenerate to change the matrix.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 py-1">
          <LabelledInput
            label="SKU"
            value={draft.sku}
            maxLength={64}
            mono
            onChange={(sku) => setDraft({ ...draft, sku })}
          />
          <LabelledInput
            label="Barcode"
            value={draft.barcode}
            maxLength={32}
            mono
            onChange={(barcode) => setDraft({ ...draft, barcode })}
          />
          {/* No quantity field: stock on hand is derived from the bundles
              this variant appears in (ADR-0061), edited in the Bundle Builder. */}
          <LabelledInput
            label="Weight (g)"
            value={draft.weightGrams}
            numeric
            onChange={(weightGrams) => setDraft({ ...draft, weightGrams })}
          />
        </div>
        <DialogFooter>
          <Button intent="ghost" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button intent="primary" size="md" onClick={() => onSave(draft)}>
            Save variant
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}

function BulkEditDialog({
  count,
  onClose,
  onApply,
}: {
  count: number;
  onClose: () => void;
  onApply: (patch: Partial<Pick<VariantDraft, "weightGrams">>) => void;
}) {
  const [weightGrams, setWeightGrams] = useState("");

  return (
    <DialogRoot open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Bulk edit {count} variants</DialogTitle>
          <DialogDescription>
            Blank fields leave the current values alone — only what you fill in is applied.
          </DialogDescription>
        </DialogHeader>
        {/* Weight only. Quantity used to live here too, until it became derived
            from the bundles (ADR-0061) — there is nothing left to set in bulk. */}
        <div className="py-1">
          <LabelledInput label="Weight (g)" value={weightGrams} numeric onChange={setWeightGrams} />
        </div>
        <DialogFooter>
          <Button intent="ghost" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button
            intent="primary"
            size="md"
            onClick={() => onApply(weightGrams.trim() ? { weightGrams: weightGrams.trim() } : {})}
          >
            Apply to {count}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}

function LabelledInput({
  label,
  value,
  onChange,
  maxLength,
  mono,
  numeric,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  maxLength?: number;
  mono?: boolean;
  numeric?: boolean;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium leading-[18px] text-ink-2">{label}</span>
      <Input
        value={value}
        {...(maxLength !== undefined ? { maxLength } : {})}
        {...(numeric ? { inputMode: "numeric" as const } : {})}
        onChange={(e) =>
          onChange(numeric ? e.target.value.replace(/[^0-9]/g, "") : e.target.value)
        }
        className={mono ? "font-mono" : ""}
      />
    </div>
  );
}
